import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import brandLogo from '../assets/images/logo.jpeg';
import useToken from '../hooks/useToken';
import { launchpadStrings } from './StringBundle';
import { useAutoLogout } from '../AutoLogoutContext';
import Tooltip from '@material-ui/core/Tooltip';
import './appSidebar.css';
import type { AppSidebarProps, Department, FlyoutPos, CSSPropertiesWithVars } from '../screens/admin/types';
import {
  FALLBACK_DEPT_COLORS,
  FLYOUT_OPEN_DELAY_MS,
  FLYOUT_CLOSE_DELAY_MS,
  getFilteredDepartments,
  getInitials,
  getModuleIcon,
  normPath,
} from '../constants/sidebarConstants';
import type { RouteItem } from '../screens/admin/types';
import { PARENT_PATH_OVERRIDES } from '../config/breadcrumbConfig';

const s = launchpadStrings;
const sidebarStrings = s.sidebar;
const SMALL_SCREEN_BREAKPOINT = 960;

const isSmallScreen = (): boolean => window.innerWidth < SMALL_SCREEN_BREAKPOINT;

// localStorage is cleared on logout, so every fresh login starts with the sidebar open.
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const readStoredCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeStoredCollapsed = (value: boolean): void => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  } catch {
    // storage unavailable — keep the in-memory state only
  }
};


// ── Component ─────────────────────────────────────────────────────────────────

export default function AppSidebar(props: AppSidebarProps) {
  const {
    current_role,
    collapsed: collapsedProp,
    setCollapsed: setCollapsedProp,
    onUserClick,
  } = props;

  const { getCurrentUserDetails } = useToken();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setProcceedButtonLabel } = useAutoLogout();
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const railRef = useRef<HTMLElement | null>(null);
  const [openDept, setOpenDept] = useState<string | null>(null);
  const [flyoutPos, setFlyoutPos] = useState<FlyoutPos>({ top: 0, left: 0 });
  const currentUserDetails = getCurrentUserDetails();
  const role: string | undefined = current_role || currentUserDetails?.role;
  /** The user's own open/closed choice; only changed by clicking, persisted across refresh, cleared on logout. */
  const [userCollapsed, setUserCollapsed] = useState<boolean>(() => readStoredCollapsed());
  const [smallScreen, setSmallScreen] = useState<boolean>(() => isSmallScreen());
  // Small screens always show the narrow rail, without changing the user's choice.
  const collapsedInternal = userCollapsed || smallScreen;
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;
  const [logoHovered, setLogoHovered] = useState<boolean>(false);

  // ── Collapse helpers ─────────────────────────────────────────────────────────

  /** Track the small-screen breakpoint (browser zoom also fires resize). */
  useEffect(() => {
    const handleResize = (): void => setSmallScreen(isSmallScreen());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapsed = (): void => {
    if (setCollapsedProp) {
      setCollapsedProp((v) => !v);
      return;
    }
    const next = !collapsedInternal;
    writeStoredCollapsed(next);
    setUserCollapsed(next);
  };

  // ── Flyout open / close ──────────────────────────────────────────────────────

  const clearOpenTimer = (): void => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  const clearCloseTimer = (): void => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openFlyoutNow = (key: string, el: Element): void => {
    clearCloseTimer();
    const rect = el.getBoundingClientRect();
    const top = Math.max(8, rect.top);
    const maxHeight = window.innerHeight - top - 8;
    setFlyoutPos({ top, left: rect.right + 8, maxHeight });
    setOpenDept(key);
  };

  /** Mouse entered a rail item: open it only if the cursor still there after the open delay. */
  const scheduleOpen = (key: string, el: Element): void => {
    clearCloseTimer();
    clearOpenTimer();
    if (openDept === key) return;
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      openFlyoutNow(key, el);
    }, FLYOUT_OPEN_DELAY_MS);
  };

  /** Mouse left a rail item or the flyout: cancel any pending open, then close after the close delay. */
  const schedClose = (): void => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpenDept(null);
    }, FLYOUT_CLOSE_DELAY_MS);
  };

  const cancelClose = (): void => clearCloseTimer();

  /** Listen for custom events that let breadcrumbs open the department flyout. */
  useEffect(() => {
    const handler = (e: Event): void => {
      const { deptKey } = (e as CustomEvent<{ deptKey?: string }>).detail || {};
      if (!deptKey || !railRef.current) return;
      const el = railRef.current.querySelector(`[data-dept-key="${deptKey}"]`);
      if (el) openFlyoutNow(deptKey, el);
    };
    document.addEventListener('ccl:open-module-menu', handler);
    return () => document.removeEventListener('ccl:open-module-menu', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Clean up any in-flight hover timers on unmount. */
  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, []);


  const departments: Department[] = useMemo(
    () => getFilteredDepartments(role),
    [role], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Active department / route detection ─────────────────────────────────────

  const { activeDeptKey, activeTo } = useMemo(() => {
    // Lowercase for case-insensitive matching (URL casing may differ from route definitions).
    const cur = normPath(pathname).toLowerCase();

    // Remap path using PARENT_PATH_OVERRIDES (for...in avoids Object.entries allocation)
    let remappedPath = cur;
    for (const prefix in PARENT_PATH_OVERRIDES) {
      const norm = normPath(prefix).toLowerCase();
      if (cur === norm) { remappedPath = normPath(PARENT_PATH_OVERRIDES[prefix]).toLowerCase(); break; }
      if (cur.startsWith(norm + '/')) { remappedPath = normPath(PARENT_PATH_OVERRIDES[prefix]).toLowerCase() + cur.slice(norm.length); break; }
    }

    // Single pass over departments + items — replaces flattenDeptItems() + reduce()
    let bestLen = -1, activeDeptKey: string | null = null, activeTo: string | null = null;
    for (const dept of departments) {
      for (const item of dept.items) {
        const to = normPath(item.to).toLowerCase();
        const len = (remappedPath === to || (to !== '/' && remappedPath.startsWith(to + '/'))) ? to.length : -1;
        if (len > bestLen) { bestLen = len; activeDeptKey = dept.key; activeTo = item.to; }
      }
    }
    return { activeDeptKey, activeTo };
  }, [pathname, departments]);


  function onRedirect(to: string): void {
    const divisionValue = sessionStorage.getItem('division');
    sessionStorage.clear();
    if (divisionValue !== null) sessionStorage.setItem('division', divisionValue);
    navigate(to);
    if (setProcceedButtonLabel) setProcceedButtonLabel(null);
  }

  // ── Named event handlers ─────────────────────────────────────────────────────

  /** Brand logo button: navigate home on large-expanded sidebar, otherwise expand. */
  function handleBrandLogoClick(): void {
    if (isSmallScreen() || !collapsed) {
      onRedirect('/home');
    } else {
      toggleCollapsed();
    }
  }

  /** Dept item-wrap mouse-enter: schedule flyout open (after a dwell delay) for non-direct-nav departments. */
  function handleDeptMouseEnter(key: string, isDirectNav: boolean, el: Element): void {
    if (!isDirectNav) scheduleOpen(key, el);
  }

  /** Dept item click: navigate for direct-nav departments (home, dashboard). */
  function handleDeptItemClick(isDirectNav: boolean, firstRoute: RouteItem): void {
    if (isDirectNav) onRedirect(firstRoute.to);
  }

  /** Dept item focus: open flyout immediately for non-direct-nav departments (keyboard nav isn't subject to the hover dwell delay). */
  function handleDeptItemFocus(isDirectNav: boolean, key: string, wrap: Element | null): void {
    if (!isDirectNav && wrap) openFlyoutNow(key, wrap);
  }

  /** Dept item keyboard: Enter/Space triggers navigation for direct-nav departments. */
  function handleDeptItemKeyDown(
    e: React.KeyboardEvent,
    isDirectNav: boolean,
    firstRoute: RouteItem,
  ): void {
    if (isDirectNav && (e.key === 'Enter' || e.key === ' ')) onRedirect(firstRoute.to);
  }

  /** Flyout card click: navigate and close the flyout. */
  function handleCardClick(to: string): void {
    onRedirect(to);
    setOpenDept(null);
  }

  /** Flyout card keyboard: Enter/Space triggers navigation. */
  function handleCardKeyDown(e: React.KeyboardEvent, to: string): void {
    if (e.key === 'Enter' || e.key === ' ') {
      onRedirect(to);
      setOpenDept(null);
    }
  }

  const userInitials = useMemo(
    () => getInitials(currentUserDetails?.name),
    [currentUserDetails?.name]
  );

  const activeFlyoutDept = useMemo(
    () => departments.find((d) => d.key === openDept),
    [departments,openDept]
  );

  if (departments.length === 0) return null;

  const flyoutDeptColors = activeFlyoutDept?.colors ?? FALLBACK_DEPT_COLORS;
  const normActiveTo = normPath(activeTo ?? undefined);


  return (
    <div className={`appsb${collapsed ? ' appsb-collapsed' : ''}`}>
      <div className="appsb-rail">

        {/* ── Brand logo + collapse toggle ── */}
        <div className="appsb-brand">
          <Tooltip title={collapsed ? sidebarStrings.expandSidebar : sidebarStrings.goToHome} arrow>
          <button
            type="button"
            className="appsb-brand-logo-btn"
            onClick={handleBrandLogoClick}
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
          >
            {collapsed && logoHovered && !isSmallScreen() ? (
              <button
              type="button"
              className="appsb-toggle"
              aria-label={sidebarStrings.expandSidebar}
              aria-expanded={true}
            >
              <ChevronRightIcon />
            </button>
            ) : (
              <img className="appsb-brand-logo" src={brandLogo} alt="Logo" />
            )}
          </button>
          </Tooltip>

          {!collapsed && (
            <Tooltip title={sidebarStrings.collapse} arrow>
            <button
              type="button"
              className="appsb-toggle"
              aria-label={sidebarStrings.collapseSidebar}
              aria-expanded={true}
              onClick={toggleCollapsed}
            >
              <ChevronLeftIcon />
            </button>
            </Tooltip>
          )}
        </div>

        {/* ── Department rail ── */}
        <nav className="appsb-rail-nav" aria-label={sidebarStrings.departmentsNav} ref={railRef}>
          {departments.map((d) => {
            const Icon = d.Icon;
            const isActive = d.key === activeDeptKey;
            const isOpen = d.key === openDept;
            const isDirectNav = d.key === 'dashboard' || d.key === 'home';
            const accentVars: CSSPropertiesWithVars = {
              '--c': d.colors.accent,
              '--tint': d.colors.tint,
              '--csh': d.colors.csh,
            };

            return (
              <div
                key={d.key}
                data-dept-key={d.key}
                className="appsb-item-wrap"
                style={accentVars}
                onMouseEnter={(e) => handleDeptMouseEnter(d.key, isDirectNav, e.currentTarget)}
                onMouseLeave={isDirectNav ? undefined : schedClose}
              >
                <div
                  className={`appsb-item${isActive ? ' appsb-active' : ''}${isOpen ? ' appsb-item-open' : ''}`}
                  tabIndex={0}
                  role="button"
                  aria-haspopup={isDirectNav ? undefined : 'true'}
                  aria-expanded={isDirectNav ? undefined : isOpen}
                  onClick={() => handleDeptItemClick(isDirectNav, d.items[0])}
                  onFocus={(e) => handleDeptItemFocus(isDirectNav, d.key, e.currentTarget.closest('.appsb-item-wrap'))}
                  onBlur={isDirectNav ? undefined : schedClose}
                  onKeyDown={(e) => handleDeptItemKeyDown(e, isDirectNav, d.items[0])}
                >
                  <span className="appsb-ic">
                    <Icon />
                  </span>
                  <span className="appsb-label">{d.name}</span>
                </div>
              </div>
            );
          })}
        </nav>

        {/* ── Footer: user card (click opens profile / logout menu) ── */}
        <div className="appsb-footer">
          {currentUserDetails?.name && (
            <Tooltip
              title={`${currentUserDetails.name} - ${currentUserDetails.role}`}
              arrow
            >
              <button
                type="button"
                className="appsb-user-card"
                onClick={onUserClick}
              >
                <div className="appsb-user-avatar">
                  {userInitials}
                </div>
                <div className="appsb-user-info">
                  <span className="appsb-user-name">{currentUserDetails.name}</span>
                  <span className="appsb-user-role">{currentUserDetails.role}</span>
                </div>
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {activeFlyoutDept &&
        createPortal(
          <div
            className="appsb-flyout appsb-flyout-open"
            style={{
              top: flyoutPos.top,
              left: flyoutPos.left,
              maxHeight: flyoutPos.maxHeight,
              '--c': flyoutDeptColors.accent,
              '--tint': flyoutDeptColors.tint,
            } as CSSPropertiesWithVars}
            role="menu"
            aria-label={activeFlyoutDept.name}
            onMouseEnter={cancelClose}
            onMouseLeave={schedClose}
          >
            <div className="appsb-fly-head">
              <span className="appsb-fly-badge">
                <activeFlyoutDept.Icon />
              </span>
              <span className="appsb-fly-title">{activeFlyoutDept.name}</span>
            </div>

            <div className="appsb-grid">
              {activeFlyoutDept.items.map((item, index) => {
                const MIcon = getModuleIcon(item.label);
                const isCardActive = normPath(item.to).toLowerCase() === normActiveTo.toLowerCase();

                return (
                  <div
                    key={`${item.to}-${index}`}
                    className={`appsb-card${isCardActive ? ' appsb-card-active' : ''}`}
                    role="menuitem"
                    tabIndex={0}
                    aria-current={isCardActive ? 'page' : undefined}
                    onClick={() => handleCardClick(item.to)}
                    onKeyDown={(e) => handleCardKeyDown(e, item.to)}
                  >
                    <span className="appsb-card-ic">
                      <MIcon />
                    </span>
                    <span className="appsb-card-label">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
